import { Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import Grid from '@mui/material/Grid';
import ProductCard from '../../../components/ProductCard/ProductCard';
import TopBar from '../../../components/TopBar/TopBar';
import { useInView } from 'react-intersection-observer'
import { Fragment, useEffect } from 'react';
import { CustomLightButton } from '../../../components/MuiStyledItems/CustomButtons';
import { getVariantModelListByBrandId, useVariantModelListByBrandId } from '../../../shared/query-hooks/VariantModelService';
import { VariantModelList, VariantModelListData } from '../../../shared/utils/interfaces/VariantModelResponse';
import { getAllBrands, getBrandBySlug, useBrandBySlug } from '../../../shared/query-hooks/BrandService';
import { useRouter } from 'next/router';
import { GetServerSidePropsContext, GetStaticPaths, NextPage } from 'next/types';
import Head from 'next/head';

interface BrandListTopBarTextProps {
    brand: string,
    modelCount: number,
}

const BrandListTopBarText = (props: BrandListTopBarTextProps) => {
    return (
        <Stack>
            <Typography align='center' variant='customBodyStrongCondensed'>{props.brand}</Typography>
            <Typography align='center' variant='customBodyXsCondensed'>{props.modelCount} models available</Typography>
        </Stack>
    )
}

interface BrandListPageProps {
    initialBrandData: VariantModelList,
    brandId: string
}

const BrandList: NextPage<BrandListPageProps> = ({ initialBrandData, brandId }) => {
    let router = useRouter();
    const { brandName } = router.query;

    const { ref, inView } = useInView()

    const { data, hasNextPage, fetchNextPage, isFetching, isFetchingNextPage, isFetchedAfterMount } = useVariantModelListByBrandId(brandId, initialBrandData);

    useEffect(() => {
        if (inView) {
            fetchNextPage()
        }
    }, [inView])

    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down('md'));

    return (
        <>
            <Head>
                <title>TRYO - {brandName?.toString().toUpperCase()}</title>
                <meta name="description" content={`Online shopping just got easier. Virtually try on ${brandName} before buying with TRYO.`} />
                <link rel="canonical" href={`${process.env.NEXT_PUBLIC_DOMAIN}/brands/${brandName}`} />
            </Head>
            <Grid container direction={"column"}>
                <Grid item>
                    <TopBar textComponent={data?.pages[0] ? <BrandListTopBarText brand={brandName as string} modelCount={initialBrandData.total_count} /> : null}></TopBar>
                </Grid>
                <Grid item container>
                    <Grid item container xs={0} md={2} lg={2.5}></Grid>
                    <Grid item container xs={12} md={8} lg={7.5} mt={{ xs: 0, md: 1 }} spacing={isSmall ? 2 : 3} justifyContent={"center"}>
                        {
                            data?.pages.map((group, i) => (
                                <Fragment key={i}>
                                    {
                                        group.data.map((variant_model: VariantModelListData) => (
                                            <Grid item key={variant_model._id}>
                                                <ProductCard variant_model={variant_model} />
                                            </Grid>
                                        ))
                                    }
                                </Fragment>
                            ))
                        }
                    </Grid>
                    <Grid item container xs={0} md={2} lg={2.5}></Grid>
                </Grid>
                <Grid item container justifyContent={"center"} py={4}>
                    {isFetching && !isFetchingNextPage ?
                        <Typography variant='customBodyCondensed'>Fetching...</Typography> :
                        <CustomLightButton ref={ref} disabled={!hasNextPage}>{hasNextPage ? "load more" : "nothing more to see"}</CustomLightButton>
                    }
                </Grid>
            </Grid>
        </>
    )
}

// SSR Query
export async function getStaticProps(context: GetServerSidePropsContext) {
    console.log('ssr render for brand detail page');
    const brandId = (await getBrandBySlug(context.params?.brandName as string, process.env.SSR_TOKEN)).data[0]._id;
    console.log('Brand SSR Params:', brandId, context.params?.brandName);

    // Prepare data for initial render
    const initialBrandData = await getVariantModelListByBrandId({ pageParam: 1 }, brandId, process.env.SSR_TOKEN);

    return {
        props: {
            initialBrandData,
            brandId
        },
        revalidate: 10
    }
}

export const getStaticPaths: GetStaticPaths = async () => {

    // To skip prerender all dynamic routes add SKIP_BUILD_STATIC_GENERATION=true to .env
    if (process.env.SKIP_BUILD_STATIC_GENERATION) {
        return {
            paths: [],
            fallback: 'blocking',
        }
    }

    const brands = await getAllBrands(process.env.SSR_TOKEN);

    return {
        paths: [
            ...brands.data.map(brand => ({ params: { brandName: brand.brand_slug } }))
        ],
        fallback: 'blocking' //indicates the type of fallback
    }
}

export default BrandList